import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useIxfDocumentsV1DocumentQueryServiceDocumentList } from '@interxfi/api-client/generated/hooks';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box, DEFAULT_BOX_PADDING } from '@interxfi/design-system/components/box/box';
import { Button } from '@interxfi/design-system/components/button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import DiagonalArrowIcon from '@interxfi/design-system/icons/react/icon-arrow-diag';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import {
  IxcRegistrationV1ControlsMarginType,
  type IxfDocumentsV1DocumentQueryServiceDocumentListDocumentListRequest,
  IxfDocumentsV1DocumentQueryServiceDocumentListFilterableFields,
} from '@interxfi/types/generated/api';
import { DocumentPageProvider } from '../../../../../../../../../features/document/document-page-context';
import { useDownloadDocument } from '../../../../../../../../../features/document/utils';
import { AccountBreadcrumb } from '../../../../../../../../../features/registration/account/account-breadcrumb';
import { AccountMarginTypePill } from '../../../../../../../../../features/registration/account/account-margin-type-pill';
import { AccountRegistrationOrPurposePill } from '../../../../../../../../../features/registration/account/account-registration-or-purpose-pill';
import { AccountStatusPill } from '../../../../../../../../../features/registration/account/account-status-pill';
import { RegistrationAccountPageHeader } from '../../../../../../../../../features/registration/account/registration-account-page-header';
import { getRegistrationAccountDetailPath } from '../../../../../../../../../features/registration/account/registration-account-routing';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/document/_document')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}`,
        label: <AccountBreadcrumb accountId={params.accountId} displayIdOnly />,
        to: getRegistrationAccountDetailPath(params.accountId),
      }),
    }) satisfies AggregateBreadcrumb,
  component: AccountDocumentLayout,
});

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const;

function buildDirectDepositInstructionsRequest(accountId: string): IxfDocumentsV1DocumentQueryServiceDocumentListDocumentListRequest {
  return {
    query: {
      filters: [
        {
          field: {
            name: IxfDocumentsV1DocumentQueryServiceDocumentListFilterableFields.AccountId,
            type: { value: accountId },
          },
        },
        {
          field: {
            name: IxfDocumentsV1DocumentQueryServiceDocumentListFilterableFields.DataSpecTypeType,
            type: { value: 'directDepositInstructions' },
          },
        },
      ],
    },
  };
}

function AccountDocumentLayout() {
  const { accountId } = Route.useParams();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const { handleDownload } = useDownloadDocument();
  const { data: directDepositInstructionResponse } = useIxfDocumentsV1DocumentQueryServiceDocumentList(
    buildDirectDepositInstructionsRequest(accountId),
  );
  const directDepositInstructionsDocumentId = directDepositInstructionResponse?.pages?.[0]?.document?.[0]?.documentId;

  return (
    <DocumentPageProvider>
      <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
        <PageLayout.Section noBorder>
          <RegistrationAccountPageHeader
            accountId={accountId}
            pills={
              <>
                <AccountStatusPill isPending={showPending} size="large" status={data?.account?.status} />
                <AccountRegistrationOrPurposePill account={data?.account} isPending={showPending} size="large" />
                <AccountMarginTypePill
                  isPending={showPending}
                  marginType={data?.account?.data?.controls?.registration?.settings?.marginType || IxcRegistrationV1ControlsMarginType.Cash}
                  size="large"
                />
              </>
            }
          />
        </PageLayout.Section>

        <PageLayout.Section>
          {directDepositInstructionsDocumentId ? (
            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: <Outlet />,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...rightColumnContainer,
                render: (
                  <Box backgroundColor="neutral-xlt" borderRadius="8px" padding={DEFAULT_BOX_PADDING}>
                    <VerticalStack gap={4}>
                      <Typography.Level3>Direct Deposit</Typography.Level3>

                      <Typography lineHeight="1.125rem" size="0.875rem">
                        Get instructions for setting up direct deposit for this account
                      </Typography>

                      <Grid autoColumns={2} gap={2}>
                        <Button
                          affixIcon={<DiagonalArrowIcon aria-hidden />}
                          fullWidth
                          onClick={() => handleDownload(directDepositInstructionsDocumentId, 'open')}
                          size="small"
                          variant="light"
                        >
                          View
                        </Button>

                        <Button
                          affixIcon={<DownloadIcon aria-hidden />}
                          fullWidth
                          onClick={() => handleDownload(directDepositInstructionsDocumentId, 'save')}
                          size="small"
                          variant="light"
                        >
                          Download
                        </Button>
                      </Grid>
                    </VerticalStack>
                  </Box>
                ),
              }}
              wrap="forward"
              wrapGap={7}
            />
          ) : (
            <Outlet />
          )}
        </PageLayout.Section>
      </PageLayout>
    </DocumentPageProvider>
  );
}
